import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faArrowDown,
	faArrowUp,
	faChevronDown,
	faChevronUp,
	faCog,
	faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';
import {
	CheckboxField,
	InputField,
	MainButton,
	SecondaryButton,
	ToggleButtonField,
} from '../../../common';
import {
	WorkflowParameterField,
	WorkflowRuleView,
} from '../../../common/services/workflowClient';

type WorkflowRuleRowProps = {
	rule: WorkflowRuleView;
	icon: IconDefinition;
	draft: Record<string, unknown>;
	open: boolean;
	saving: boolean;
	canMoveUp: boolean;
	canMoveDown: boolean;
	onToggleEnabled: (enabled: boolean) => void;
	onMove: (direction: -1 | 1) => void;
	onToggleParameters: () => void;
	onDraftChange: (key: string, value: unknown) => void;
	onSaveParameters: () => void;
};

const iconButtonClass =
	'inline-flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-30 disabled:hover:bg-transparent';

const WorkflowRuleRow: React.FC<WorkflowRuleRowProps> = ({
	rule,
	icon,
	draft,
	open,
	saving,
	canMoveUp,
	canMoveDown,
	onToggleEnabled,
	onMove,
	onToggleParameters,
	onDraftChange,
	onSaveParameters,
}) => {
	const dirty =
		JSON.stringify(draft ?? {}) !== JSON.stringify(rule.parameters ?? {});

	return (
		<article className="flex bg-white">
			<div
				className={`flex w-14 shrink-0 justify-center self-stretch border-l-4 border-r pt-4 ${
					rule.enabled
						? 'border-l-blue-500 border-r-blue-100 bg-blue-50'
						: 'border-l-slate-300 border-r-slate-200 bg-slate-50'
				}`}
			>
				<FontAwesomeIcon
					icon={icon}
					className={`h-5 w-5 ${rule.enabled ? 'text-blue-600' : 'text-slate-400'}`}
				/>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-start gap-4 px-4 py-3">
					<div className="min-w-0 flex-1">
						<h3
							className={`text-base font-medium ${rule.enabled ? 'text-slate-900' : 'text-slate-500'}`}
						>
							{rule.label}
						</h3>
						<p
							className={`mt-1 text-sm ${rule.enabled ? 'text-slate-600' : 'text-slate-400'}`}
						>
							{rule.description}
						</p>
					</div>
					<div className="flex shrink-0 items-center gap-1">
						<button
							type="button"
							className={iconButtonClass}
							aria-label="Monter"
							disabled={!canMoveUp || saving}
							onClick={() => onMove(-1)}
						>
							<FontAwesomeIcon icon={faArrowUp} className="h-3.5 w-3.5" />
						</button>
						<button
							type="button"
							className={iconButtonClass}
							aria-label="Descendre"
							disabled={!canMoveDown || saving}
							onClick={() => onMove(1)}
						>
							<FontAwesomeIcon icon={faArrowDown} className="h-3.5 w-3.5" />
						</button>
						<button
							type="button"
							className={`${iconButtonClass} w-auto gap-1 px-2 ${open ? 'text-blue-600' : ''}`}
							aria-label="Paramètres"
							aria-expanded={open}
							onClick={onToggleParameters}
						>
							<FontAwesomeIcon icon={faCog} className="h-4 w-4" />
							<FontAwesomeIcon
								icon={open ? faChevronUp : faChevronDown}
								className="h-3 w-3"
							/>
						</button>
						<ToggleButtonField
							label="Active"
							isOn={rule.enabled}
							onChange={onToggleEnabled}
							size="sm"
							loading={saving}
							invert
						/>
					</div>
				</div>
				{open && (
					<div className="border-t border-blue-100 bg-gray-200 px-4 py-4">
						{rule.schema.length === 0 ? (
							<p className="text-sm text-gray-500">Aucun paramètre.</p>
						) : (
							<div className="space-y-4">
								{rule.schema.map((field) => (
									<ParameterField
										key={field.key}
										field={field}
										value={draft?.[field.key]}
										onChange={(value) => onDraftChange(field.key, value)}
									/>
								))}
							</div>
						)}
						<div className="mt-4 flex items-center justify-between gap-3">
							<SecondaryButton
								label="Replier"
								icon={faChevronUp}
								onClick={onToggleParameters}
								size="md"
							/>
							{rule.schema.length > 0 && (
								<MainButton
									label="Enregistrer"
									icon={faFloppyDisk}
									onClick={onSaveParameters}
									size="md"
									disabled={!dirty || saving}
								/>
							)}
						</div>
					</div>
				)}
			</div>
		</article>
	);
};

const ParameterField: React.FC<{
	field: WorkflowParameterField;
	value: unknown;
	onChange: (value: unknown) => void;
}> = ({ field, value, onChange }) => {
	if (field.type === 'boolean') {
		return (
			<ToggleButtonField
				label={field.label}
				isOn={Boolean(value)}
				onChange={onChange}
				size="sm"
				invert
			/>
		);
	}

	if (field.type === 'text') {
		return (
			<InputField
				label={field.label}
				name={field.key}
				value={typeof value === 'string' ? value : ''}
				onChange={(event) => onChange(event.target.value)}
			/>
		);
	}

	if (field.type === 'status_list') {
		const selected = Array.isArray(value) ? (value as string[]) : [];
		return (
			<fieldset>
				<legend className="mb-2 text-sm font-medium text-gray-700">
					{field.label}
				</legend>
				<div className="flex flex-wrap gap-x-4 gap-y-2">
					{(field.options ?? []).map((option) => (
						<CheckboxField
							key={option.value}
							label={option.label}
							size="sm"
							checked={selected.includes(option.value)}
							onChange={(checked) => {
								const next = checked
									? [...selected, option.value]
									: selected.filter((item) => item !== option.value);
								onChange(next);
							}}
						/>
					))}
				</div>
			</fieldset>
		);
	}

	const transitions = (
		value && typeof value === 'object' ? value : {}
	) as Record<string, string[]>;

	return (
		<fieldset>
			<legend className="mb-2 text-sm font-medium text-gray-700">
				{field.label}
			</legend>
			<div className="space-y-3">
				{(field.statuses ?? []).map((from) => (
					<div key={from.value}>
						<p className="text-sm font-medium text-gray-800">{from.label}</p>
						<div className="mt-1 flex flex-wrap gap-x-4 gap-y-2">
							{(field.statuses ?? [])
								.filter((to) => to.value !== from.value)
								.map((to) => {
									const targets = transitions[from.value] ?? [];
									return (
										<CheckboxField
											key={to.value}
											label={to.label}
											size="sm"
											checked={targets.includes(to.value)}
											onChange={(checked) => {
												const nextTargets = checked
													? [...targets, to.value]
													: targets.filter((item) => item !== to.value);
												onChange({
													...transitions,
													[from.value]: nextTargets,
												});
											}}
										/>
									);
								})}
						</div>
					</div>
				))}
			</div>
		</fieldset>
	);
};

export default WorkflowRuleRow;
